import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import useAgency from 'features/Agency/useAgency';
import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import KTFormInput, {
  KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTRadio, {
  KTRadioType,
} from 'general/components/OtherKeenComponents/KTFormControls/KTRadio';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import CustomDropdown2 from 'general/components/Others/CustomDropdown2';
import AppColors from 'general/constants/AppColors';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

DialogAddPricingView.propTypes = {
  item: PropTypes.object,
  onCancel: PropTypes.func,
  onDone: PropTypes.func,
};

function DialogAddPricingView({ item, onCancel, onDone }) {
  // MARK: ---- PROPERTIES ----
  const { user } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const editMode = !!item;
  const [loading, setLoading] = useState(false);
  const [readyToValidate, setReadyToValidate] = useState(false);
  const [valuationObjectType, setValuationObjectType] = useState('CHARGE_POINT');
  const { operatorList, operatorOptions } = useAgency();
  const [selectedOperatorId, setSelectedOperatorId] = useState('');
  const [selectedChargePoint, setSelectedChargePoint] = useState(null);
  const dropdownChargePointEl = useMemo(() => {
    return (
      <CustomDropdown2
        id="dialog_pointId"
        name="dialog_pointId"
        toggleElement={
          <button
            className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between`}
            type="button"
          >
            {selectedChargePoint?.name ?? 'Chọn trụ sạc'}
          </button>
        }
        selectedValue={selectedChargePoint}
        dropdownMenuItems={[{ name: 'Trụ sạc', role: 'CHARGE_POINT' }]}
        getSubItems={async (item) => {
          return ChargeHelper.API.requestGetChargePointList({
            page: item?.page || 0,
            limit: 10,
            q: item?.q || '',
          }).then((data) => {
            return {
              values: data.chargePoints?.map((row) => ({
                id: row?.pointId,
                name: row?.pointCode,
                item: row,
              })),
              total: data.total,
            };
          });
        }}
        dropdownMenuStyle={{ maxHeight: '30vh' }}
        dropdownMenuClassName="overflow-auto w-100"
        onChange={(value) => {
          setSelectedChargePoint(value);
        }}
        isDropdownForm={true}
        titleSearchable={true}
        searchable={true}
        searchFormClassName="mx-3 my-1 search-form-border"
        ignoreConfirmClick={true}
        defaultValue={{
          name: 'Trụ sạc',
          role: 'CHARGE_POINT',
        }}
        footerEl={
          <div
            className="p-4 position-sticky sticky-bottom bg-white"
            style={{
              borderTop: `1px solid ${AppColors.gray[300]}`,
            }}
          >
            {UIHelper.buildNegativeButton('Bỏ chọn', () => {
              setSelectedChargePoint(null);
            })}
          </div>
        }
      />
    );
  }, [selectedChargePoint]);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const dropdownInvestorEl = useMemo(() => {
    return (
      <CustomDropdown2
        id="dialog_investorId"
        name="dialog_investorId"
        toggleElement={
          <button
            className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between`}
            type="button"
          >
            {selectedInvestor?.name ?? 'Chọn nhà đầu tư'}
          </button>
        }
        selectedValue={selectedInvestor}
        subTitleType="email"
        dropdownMenuItems={[{ name: 'Nhà đầu tư NQ', role: 'INVESTOR' }]}
        getSubItems={async (item) => {
          return AgencyHelper.API.requestGetAgencyList({
            role: AgencyConstants.AgencyRole.INVESTOR,
            page: item?.page || 0,
            limit: 10,
            q: item?.q || '',
          }).then((data) => {
            return {
              values: data.agencies?.map((row) => ({
                id: row?.agencyId,
                name: row?.name,
                item: row,
                email: row?.email,
              })),
              total: data.total,
            };
          });
        }}
        dropdownMenuStyle={{ maxHeight: '30vh' }}
        dropdownMenuClassName="overflow-auto w-100"
        onChange={(value) => {
          setSelectedInvestor(value);
        }}
        isDropdownForm={true}
        titleSearchable={true}
        searchable={true}
        searchFormClassName="mx-3 my-1 search-form-border"
        ignoreConfirmClick={true}
        defaultValue={{
          name: 'Nhà đầu tư NQ',
          role: 'INVESTOR',
        }}
        footerEl={
          <div
            className="p-4 position-sticky sticky-bottom bg-white"
            style={{
              borderTop: `1px solid ${AppColors.gray[300]}`,
            }}
          >
            {UIHelper.buildNegativeButton('Bỏ chọn', () => {
              setSelectedInvestor(null);
            })}
          </div>
        }
      />
    );
  }, [selectedInvestor]);
  const [pricingMode, setPricingMode] = useState(ChargeConstants.ChargePricingMode.FLAT);
  const [flatPrice, setFlatPrice] = useState('');
  const [normalPrice, setNormalPrice] = useState('');
  const [peakPrice, setPeakPrice] = useState('');
  const [offPeakPrice, setOffPeakPrice] = useState('');
  const [freeOverchargeDuration, setFreeOverchargeDuration] = useState('');
  const [overchargePrice, setOverchargePrice] = useState('');
  const [reservationFee, setReservationFee] = useState('');
  const [reservationFreeCancellation, setReservationFreeCancellation] = useState('');
  const [taxRate, setTaxRate] = useState('8');
  const taxRateOptions = [
    { value: '10', name: '10%' },
    { value: '8', name: '8%' },
    { value: '5', name: '5%' },
    { value: '0', name: '0%' },
    { value: 'KCT', name: 'KCT' },
  ];
  useEffect(() => {
    if (item) {
      if (!_.isNil(item?.pointId)) {
        setValuationObjectType('CHARGE_POINT');
        setSelectedChargePoint({
          id: item?.pointId,
          name: item?.point?.pointCode,
          item: item?.point,
        });
      } else if (!_.isNil(item?.agencyId)) {
        setValuationObjectType('INVESTOR');
        setSelectedInvestor({
          id: item?.agencyId,
          name: item?.agency?.name,
          item: item?.agency,
        });
      } else if (!_.isNil(item?.operatorId)) {
        setValuationObjectType('OPERATOR');
        setSelectedOperatorId(item?.operatorId);
      }

      setPricingMode(item?.pricingMode);
      setFlatPrice(item?.flatPrice);
      setNormalPrice(item?.normalPrice);
      setPeakPrice(item?.peakPrice);
      setOffPeakPrice(item?.offPeakPrice);
      setFreeOverchargeDuration(item?.freeOverchargeDuration / 60);
      setOverchargePrice(item?.overchargePrice);
      setReservationFee(item?.reservationFee);
      setReservationFreeCancellation(item?.reservationFreeCancellation);
      setTaxRate(item?.taxRate != null ? String(item.taxRate).replace('%', '') : '8');
    }
  }, [item]);
  useEffect(() => {
    if (!isAdmin) {
      setValuationObjectType('INVESTOR');
      setSelectedInvestor({
        id: user?.agencyId,
        name: user?.agency?.name,
        item: user?.agency,
      });
    }
  }, [user]);

  // MARK: ---- METHODS ----
  const handleSubmit = async () => {
    setReadyToValidate(true);

    if (valuationObjectType === 'CHARGE_POINT' && !selectedChargePoint) {
      ToastHelper.showError('Vui lòng chọn trụ sạc');
      return;
    }
    if (valuationObjectType === 'INVESTOR' && !selectedInvestor) {
      ToastHelper.showError('Vui lòng chọn nhà đầu tư');
      return;
    }
    if (valuationObjectType === 'OPERATOR' && !selectedOperatorId) {
      ToastHelper.showError('Vui lòng chọn nhà điều hành');
      return;
    }
    if (!Utils.isValid(overchargePrice) || !Utils.isValid(freeOverchargeDuration)) {
      ToastHelper.showError('Vui lòng nhập đầy đủ thông tin cấu hình giá');
      return;
    }
    if (pricingMode === ChargeConstants.ChargePricingMode.FLAT && !Utils.isValid(flatPrice)) {
      ToastHelper.showError('Vui lòng nhập đầy đủ thông tin giá điện');
      return;
    }
    if (
      pricingMode === ChargeConstants.ChargePricingMode.HOURS &&
      (!Utils.isValid(normalPrice) || !Utils.isValid(peakPrice) || !Utils.isValid(offPeakPrice))
    ) {
      ToastHelper.showError('Vui lòng nhập đầy đủ thông tin giá điện');
      return;
    }
    if (!Utils.isValid(reservationFee) || !Utils.isValid(reservationFreeCancellation)) {
      ToastHelper.showError('Vui lòng nhập đầy đủ thông tin đặt chỗ trước');
      return;
    }

    let params = {
      pricingMode: pricingMode,
      freeOverchargeDuration: _.toNumber(freeOverchargeDuration) * 60,
      overchargePrice: overchargePrice,
      reservationFee: reservationFee,
      reservationFreeCancellation: reservationFreeCancellation,
      currency: 'VNĐ',
      taxRate: taxRate,
    };
    if (pricingMode === ChargeConstants.ChargePricingMode.FLAT) {
      params.flatPrice = flatPrice;
    } else if (pricingMode === ChargeConstants.ChargePricingMode.HOURS) {
      params.normalPrice = normalPrice;
      params.peakPrice = peakPrice;
      params.offPeakPrice = offPeakPrice;
    }
    if (valuationObjectType === 'OPERATOR') {
      params.operatorId = selectedOperatorId;
    } else if (valuationObjectType === 'CHARGE_POINT') {
      params.pointId = selectedChargePoint?.id;
    } else if (valuationObjectType === 'INVESTOR') {
      params.agencyId = selectedInvestor?.id;
    }
    // console.log(params);
    // return;
    setLoading(true);
    if (!editMode) {
      const res = await ChargeHelper.API.requestCreateChargePricing(params);
      if (_.isBoolean(res) && res) {
        ToastHelper.showSuccess('Thêm mới cấu hình giá thành công');
        onDone && onDone();
      } else if (_.isString(res)) {
        ToastHelper.showError(res);
      }
    } else {
      const res = await ChargeHelper.API.requestUpdateChargePricing(item?.pricingId, params);
      if (_.isBoolean(res) && res) {
        ToastHelper.showSuccess('Cập nhật cấu hình giá thành công');
        onDone && onDone();
      } else if (_.isString(res)) {
        ToastHelper.showError(res);
      }
    }
    setLoading(false);
  };

  return (
    <div>
      <div className="px-4 mt-4">
        {UIHelper.buildAlert(
          <i
            className="fa-duotone fa-circle-info fa-2x"
            style={{ color: 'rgba(41, 132, 238, 1)' }}
          />,
          'Đối tượng định giá',
          <>
            Chọn{' '}
            <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[600])}>
              “Trụ sạc”{' '}
            </span>
            để thiết lập mức giá áp dụng cho riêng một trụ sạc. <br />
            Chọn{' '}
            <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[600])}>
              “Nhà đầu tư”{' '}
            </span>
            để thiết lập mức giá hộ cho một “Nhà đầu tư”. <br />
            Chọn{' '}
            <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[600])}>
              “Nhà điều hành”{' '}
            </span>
            để thiết lập mức giá áp dụng cho toàn hệ thống.
          </>,
          'rgba(41, 132, 238, 1)',
          'rgba(246, 252, 255, 1)'
        )}
      </div>

      {/* doi tuong dinh gia */}
      {isAdmin && (
        <div className="mt-4">
          {UIHelper.buildCardHeader('Đối tượng định giá', false, null, true)}
          <div className="radio-inline w-100 px-5 mb-4">
            <div className="d-flex align-items-center gap-5">
              <KTRadio
                name="valuationObjectPoint"
                label="Trụ sạc"
                type={KTRadioType.outline}
                additionalClassName="radio-success"
                onChange={(value) => {
                  setValuationObjectType('CHARGE_POINT');
                }}
                checked={valuationObjectType === 'CHARGE_POINT'}
              />
              <KTRadio
                name="valuationObjectInvestor"
                label="NĐT nhượng quyền"
                type={KTRadioType.outline}
                additionalClassName="radio-success"
                onChange={(value) => {
                  setValuationObjectType('INVESTOR');
                }}
                checked={valuationObjectType === 'INVESTOR'}
              />
              <KTRadio
                name="valuationObjectLandlord"
                label="NĐT Liên kết"
                type={KTRadioType.outline}
                additionalClassName="radio-success"
                onChange={(value) => {
                  setValuationObjectType('LANDLORD');
                }}
                checked={valuationObjectType === 'LANDLORD'}
                disabled
              />
              <KTRadio
                name="valuationObjectAdmin"
                label="Nhà điều hành"
                type={KTRadioType.outline}
                additionalClassName="radio-success"
                onChange={(value) => {
                  setValuationObjectType('OPERATOR');
                }}
                checked={valuationObjectType === 'OPERATOR'}
              />
            </div>
          </div>
        </div>
      )}
      {isAdmin && (
        <div
          className="row m-0 p-0 px-2 mt-4"
          style={{
            borderBottom: `1px dashed ${AppColors.gray[300]}`,
          }}
        >
          <div className="col-lg-6 mb-4 px-2">
            {valuationObjectType === 'OPERATOR' && (
              <div>
                {UIHelper.buildFormLabel('Nhà điều hành', true)}
                <KTBSDropdown
                  name="operatorId"
                  toggleElement={
                    <button
                      className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                      type="button"
                    >
                      {_.find(operatorList, (item) => item?.operatorId === selectedOperatorId)
                        ?.name ?? 'Tất cả'}
                    </button>
                  }
                  selectedValue={selectedOperatorId}
                  dropdownMenuItems={[{ name: 'Tất cả', value: '' }, ...operatorOptions]}
                  dropdownMenuClassName="overflow-auto w-100"
                  dropdownMenuStyle={{ maxHeight: '30vh' }}
                  onChange={(value) => {
                    setSelectedOperatorId(value);
                  }}
                  isDropdownForm={true}
                />
              </div>
            )}
            {valuationObjectType === 'CHARGE_POINT' && (
              <div>
                {UIHelper.buildFormLabel('Trụ sạc', true)}
                {dropdownChargePointEl}
              </div>
            )}
            {valuationObjectType === 'INVESTOR' && (
              <div>
                {UIHelper.buildFormLabel('Nhà đầu tư NQ', true)}
                {dropdownInvestorEl}
              </div>
            )}
          </div>
        </div>
      )}

      {/* cau hinh gia */}
      <div className="mt-2">{UIHelper.buildCardHeader('Cấu hình giá', false, null, true)}</div>
      <div
        className="row m-0 p-0 px-2"
        style={{
          borderBottom: `1px dashed ${AppColors.gray[300]}`,
        }}
      >
        <div className="col-lg-6 mb-4 px-2">
          <div>
            <div className='d-flex gap-2'>
              {UIHelper.buildFormLabel('Đơn giá sạc quá giờ', true, false)}
              <KTTooltip
                text={`<div>Là số chi phí khách hàng phải trả cho mỗi phút chưa rút vòi sạc ra khỏi xe và đặt lại về trụ sạc</div>`}
                textHeader="Đơn giá sạc quá giờ "
                isHtmlContent
              >
                <i
                  className="fa-regular fa-info-circle"
                  style={{ color: AppColors.info }}
                />
              </KTTooltip>
            </div>
            <KTFormInput
              type={KTFormInputType.currency}
              unit="VNĐ/phút"
              placeholder="Nhập đơn giá sạc quá giờ"
              name="overchargePrice"
              value={overchargePrice}
              onChange={(value) => {
                setOverchargePrice(value);
              }}
            />
          </div>
        </div>
        <div className="col-lg-6 mb-4 px-2">
          <div>
            <div className='d-flex gap-2'>
              {UIHelper.buildFormLabel('Thời gian chờ miễn phí', true, false)}
              <KTTooltip
                text={`<div>Thời gian chờ miễn phí là thời gian sau khi xe đã sạc đầy nhưng chủ xe chưa đến hoàn thành rút vòi sạc ra khỏi xe và đặt lại về trụ sạc</div>`}
                textHeader="Thời gian chờ miễn phí "
                isHtmlContent
              >
                <i
                  className="fa-regular fa-info-circle"
                  style={{ color: AppColors.info }}
                />
              </KTTooltip>
            </div>
            <KTFormInput
              type={KTFormInputType.currency}
              unit="Phút"
              placeholder="Nhập thời gian chờ miễn phí"
              name="freeOverchargeDuration"
              value={freeOverchargeDuration}
              onChange={(value) => {
                setFreeOverchargeDuration(value);
              }}
            />
          </div>
        </div>
      </div>

      {/* co che tinh gia dien */}
      <div className="mt-2">
        {UIHelper.buildCardHeader('Cơ chế tính giá sạc', false, null, true)}
      </div>
      <div className="row m-0 p-0 px-2"
        style={{
          borderBottom: `1px dashed ${AppColors.gray[300]}`,
        }}
      >
        <div className="col-lg-6 mb-4 px-2">
          <div>
            {UIHelper.buildFormLabel('Phương thức tính giá', true)}
            <KTBSDropdown
              name="pricingMode"
              toggleElement={
                <button
                  className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between`}
                  type="button"
                >
                  {pricingMode
                    ? _.find(
                      ChargeHelper.Data.chargePointPricingModeOptions,
                      (item) => item?.value === pricingMode
                    )?.name
                    : 'Chọn cách tính giá điện'}
                </button>
              }
              selectedValue={pricingMode}
              dropdownMenuItems={ChargeHelper.Data.chargePointPricingModeOptions}
              dropdownMenuClassName="overflow-auto w-100"
              dropdownMenuStyle={{ maxHeight: '30vh' }}
              onChange={(value) => {
                setPricingMode(value);
              }}
              isDropdownForm={true}
            />
          </div>
        </div>
        {pricingMode === ChargeConstants.ChargePricingMode.FLAT && (
          <div className="col-lg-6 mb-4 px-2">
            <div>
              <div className='d-flex gap-2'>
                {UIHelper.buildFormLabel('Giá sạc cố định', true, false)}
              </div>
              <KTFormInput
                type={KTFormInputType.currency}
                unit="VNĐ/kWh"
                placeholder="Nhập giá sạc cố định"
                name="flatPrice"
                value={flatPrice}
                onChange={(value) => {
                  setFlatPrice(value);
                }}
              />
            </div>
          </div>
        )}
        {pricingMode === ChargeConstants.ChargePricingMode.HOURS && (
          <>
            <div className="col-lg-6 mb-4 px-2">
              <div>
                <div className='d-flex gap-2'>
                  {UIHelper.buildFormLabel('Giá giờ bình thường', true, false)}
                  <KTTooltip
                    text={`<div><div>• Từ thứ 2 đến thứ 7: 06:00 - 17:30, 22:30 - 24:00</div>
                          <div>• Chủ nhật: 06:00 - 24:00</div></div>`}
                    textHeader="Giờ bình thường"
                    isHtmlContent
                  >
                    <i
                      className="fa-regular fa-info-circle"
                      style={{ color: AppColors.info }}
                    />
                  </KTTooltip>
                </div>
                <KTFormInput
                  type={KTFormInputType.currency}
                  unit="VNĐ/kWh"
                  placeholder="Nhập giá giờ bình thường"
                  name="normalPrice"
                  value={normalPrice}
                  onChange={(value) => {
                    setNormalPrice(value);
                  }}
                />
              </div>
            </div>
            <div className="col-lg-6 mb-4 px-2">
              <div>
                <div className='d-flex gap-2'>
                  {UIHelper.buildFormLabel('Giá giờ cao điểm', true, false)}
                  <KTTooltip
                    text={`<div><div>Từ thứ 2 đến thứ 7: 17:30 - 22:30</div>
                        <div>Chủ nhật: không có</div></div>`}
                    textHeader="Giờ cao điểm"
                    isHtmlContent
                  >
                    <i
                      className="fa-regular fa-info-circle"
                      style={{ color: AppColors.info }}
                    />
                  </KTTooltip>
                </div>
                <KTFormInput
                  type={KTFormInputType.currency}
                  unit="VNĐ/kWh"
                  placeholder="Nhập giá giờ cao điểm"
                  name="peakPrice"
                  value={peakPrice}
                  onChange={(value) => {
                    setPeakPrice(value);
                  }}
                />
              </div>
            </div>
            <div className="col-lg-6 mb-4 px-2">
              <div>
                <div className='d-flex gap-2'>
                  {UIHelper.buildFormLabel('Giá giờ thấp điểm', true, false)}
                  <KTTooltip
                    text={`<div>Từ 00:00 - 06:00 tất cả các ngày trong tuần</div>`}
                    textHeader="Giờ thấp điểm"
                    isHtmlContent
                  >
                    <i
                      className="fa-regular fa-info-circle"
                      style={{ color: AppColors.info }}
                    />
                  </KTTooltip>
                </div>
                <KTFormInput
                  type={KTFormInputType.currency}
                  unit="VNĐ/kWh"
                  placeholder="Nhập giá giờ thấp điểm"
                  name="offPeakPrice"
                  value={offPeakPrice}
                  onChange={(value) => {
                    setOffPeakPrice(value);
                  }}
                />
              </div>
            </div>
          </>
        )}
        {/* Thuế suất - hiển thị cho tất cả các chế độ giá */}
        <div className="col-lg-6 mb-4 px-2">
          <div>
            {UIHelper.buildFormLabel('Thuế suất', true)}
            <KTBSDropdown
              name="taxRate"
              toggleElement={
                <button
                  className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                  type="button"
                >
                  {taxRate != null ? (taxRate === 'KCT' ? 'KCT' : `${taxRate}%`) : 'Chọn thuế suất'}
                </button>
              }
              selectedValue={taxRate}
              dropdownMenuItems={taxRateOptions}
              dropdownMenuClassName="overflow-auto w-100"
              dropdownMenuStyle={{ maxHeight: '30vh' }}
              onChange={(value) => {
                setTaxRate(value);
              }}
              isDropdownForm={true}
            />
          </div>
        </div>
      </div>

      {/* dat cho truoc */}
      <div className="mt-2">{UIHelper.buildCardHeader('Đặt chỗ trước', false, null, true)}</div>
      <div className="row m-0 p-0 px-2">
        <div className="col-lg-6 mb-4 px-2">
          <div>
            <div className='d-flex gap-2'>
              {UIHelper.buildFormLabel('Phí đặt giữ chỗ', true, false)}
              <KTTooltip
                text={`<div>Chi phí khi tài xế hủy giữ chỗ sau thời gian hủy miễn phí hoặc không đến sạc.</div>`}
                textHeader="Phí đặt giữ chỗ"
                isHtmlContent
              >
                <i
                  className="fa-regular fa-info-circle"
                  style={{ color: AppColors.info }}
                />
              </KTTooltip>
            </div>
            <KTFormInput
              type={KTFormInputType.currency}
              unit="VNĐ"
              placeholder="Nhập phí đặt giữ chỗ"
              name="reservationFee"
              value={reservationFee}
              onChange={(value) => {
                setReservationFee(value);
              }}
            />
          </div>
        </div>
        <div className="col-lg-6 mb-4 px-2">
          <div>
            <div className='d-flex gap-2'>
              {UIHelper.buildFormLabel('Thời gian hủy giữ chỗ miễn phí', true, false)}
              <KTTooltip
                text={`<div>Thời gian tài xế được hủy giữ chỗ mà không mất phí đặt giữ chỗ, tính từ thời điểm đặt chỗ thành công (phút)</div>`}
                textHeader="Thời gian hủy giữ chỗ miễn phí"
                isHtmlContent
              >
                <i
                  className="fa-regular fa-info-circle"
                  style={{ color: AppColors.info }}
                />
              </KTTooltip>
            </div>
            <KTFormInput
              type={KTFormInputType.currency}
              unit="Phút"
              placeholder="Nhập thời gian hủy giữ chỗ miễn phí"
              name="reservationFreeCancellation"
              value={reservationFreeCancellation}
              onChange={(value) => {
                setReservationFreeCancellation(value);
              }}
            />
          </div>
        </div>
      </div>

      {/* actions */}
      <div
        className="d-flex align-items-center justify-content-end gap-3 px-4 py-3"
        style={{
          borderTop: `1px solid ${AppColors.gray[200]}`,
        }}
      >
        {UIHelper.buildNegativeButton('Hủy bỏ', onCancel, null)}
        {UIHelper.buildGreenButton(
          !editMode ? 'Thêm' : 'Cập nhật',
          handleSubmit,
          null,
          false,
          loading
        )}
      </div>
    </div>
  );
}

export default DialogAddPricingView;
